import { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useUiContext } from '../../context/UiContext';
import { useFormConfig } from '../../hooks/useFormConfig';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import DCFMarkdown from '../DCFMarkdown/DCFMarkdown';
import Footer from '../form/Footer';
import PhaseBanner from '../form/PhaseBanner';
import Header from '../Header/Header';
import ServiceNavigation from '../ServiceNavigation/ServiceNavigation';
import type { Props } from './types';

const Container = styled.div.withConfig({
  shouldForwardProp: (prop) => !['loading'].includes(prop),
})<{ loading?: boolean }>`
    position: relative;

    &.app-width-container--unlimited {
        width: fit-content;
        min-width: min(960px, calc(100vw - 2cm));
        max-width: calc(100vw - 2cm);
        margin-inline: auto;
    }

    &:after {
        content: ${(p) => (p.loading ? "''" : 'normal')};
        position: absolute;
        inset: 0px;
        overflow: hidden;
        height: 100%;
        width: 100%;
        z-index: 100;
        transition: background-color 800ms ease-in-out 0s;
        will-change: background-color;
        background-color: rgba(255, 255, 255, 0.85);
    }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.85);
`;

const Spinner = styled.div`
  width: 96px;
  height: 96px;
  border: 10px solid #b1b4b6;
  border-top-color: #1d70b8;
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
  margin: 0 auto;
`;

const LoadingLabel = styled.p`
  margin-top: 16px;
  margin-bottom: 0;
  text-align: center;
`;

function PageTemplate({
  children,
  breadcrumbs,
  title,
  titleSuffix,
  titleRight,
  titleClassName,
  subTitle,
  beforeTitle,
  fullWidth = false,
  limitWidth: pageLimitWidth,
  backLink,
  forceLoadingOverlay = false,
  suppressLoadingOverlay = false,
}: Props) {
  const { isLoading } = useUiContext();
  const { formConfig } = useFormConfig();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const showLoadingOverlay = (isLoading || forceLoadingOverlay) && !suppressLoadingOverlay;
  const limitWidth = pageLimitWidth ?? (formConfig.limitWidth !== false);
  const useFullWidth = fullWidth || !limitWidth;

  useEffect(() => {
    titleRef.current?.focus();
  }, [title]);

  return (
    <>
      <a href="#main-content" className="govuk-skip-link" data-module="govuk-skip-link">
        Skip to main content
      </a>

      <Header loading={isLoading} />
      <ServiceNavigation />

      <Container
        className={`govuk-width-container${limitWidth ? '' : ' app-width-container--unlimited'}`}
        loading={showLoadingOverlay}
      >
        <PhaseBanner />
        {breadcrumbs && <Breadcrumbs {...breadcrumbs} />}
        {backLink && (
          <nav>
            <a className="govuk-back-link" href={backLink}>
              Back
            </a>
          </nav>
        )}
        <main className="govuk-main-wrapper" id="main-content">
          <div className="govuk-grid-row">
            <div
              className={useFullWidth ? 'govuk-grid-column-full' : 'govuk-grid-column-two-thirds'}
              data-testid="pageWidth"
              aria-live="polite"
              aria-busy={Boolean(showLoadingOverlay).toString() as 'true' | 'false'}
            >
              {beforeTitle}
              {title && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: titleRight ? 'flex-start' : 'space-between',
                    alignItems: 'flex-start',
                    gap: titleRight ? 64 : 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <h1
                    className={`govuk-heading-xl${titleClassName ? ` ${titleClassName}` : ''}`}
                    ref={titleRef}
                    tabIndex={-1}
                  >
                    {subTitle && <DCFMarkdown content={subTitle} type="subtitle" />}
                    {title}
                    {titleSuffix}
                  </h1>
                  {titleRight}
                </div>
              )}
              {children}
            </div>
          </div>
        </main>
      </Container>
      <Footer />
      {showLoadingOverlay && (
        <LoadingOverlay role="status" aria-live="polite" aria-label="Processing request">
          <div>
            <Spinner />
            <LoadingLabel className="govuk-body">Processing...</LoadingLabel>
          </div>
        </LoadingOverlay>
      )}
    </>
  );
}

export default PageTemplate;
